import { useMutation, useMutationState, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { useToast } from '../../platform/feedback/ToastProvider'
import { emitLifecycle } from './recurringAnalytics'
import type { LocalDate } from './recurringRules'
import { toBill, type Bill, type Entry } from './recurringWire'

/** S13, verbatim. */
export const CONFLICT_COPY = 'This bill changed on another device. Showing the latest.'
export const DELETED_COPY = 'Deleted'
export const PAUSE_FAILED = "Couldn't pause it. Try again."
export const RESUME_FAILED = "Couldn't resume it. Try again."
export const SKIP_FAILED = "Couldn't skip it. Try again."
export const DELETE_FAILED = "Couldn't delete it. Try again."
export const BUSY_COPY = 'Another change to this bill is still saving. Try again in a moment.'

/**
 * Why a lifecycle write did not happen, as a caller needs it. The `message` is
 * the sentence a confirm dialog can show as it stands.
 *
 * - `conflict` — 409 on the pause PATCH: the bill moved on another device (S13).
 *   Already toasted and already refetched; never retried here.
 * - `busy` — another write to this bill is out; nothing was sent.
 * - `failed` — anything else.
 */
export class LifecycleError extends Error {
  readonly kind: 'conflict' | 'busy' | 'failed'
  constructor(kind: LifecycleError['kind'], message: string, cause?: unknown) {
    super(message, { cause })
    this.name = 'LifecycleError'
    this.kind = kind
  }
}

type Op =
  | { op: 'skip'; periodDueDate: LocalDate }
  /** `quiet`: the caller shows the failure itself (Bill settings), so no toast. */
  | { op: 'pause'; paused: boolean; quiet?: boolean }
  | { op: 'delete' }

/** Rewrite `id`'s bill in the list, the detail and every calendar month: the three places a screen reads it. */
function patchBill(client: QueryClient, id: string, fn: (b: Bill) => Bill): void {
  const touch = (b: Bill): Bill => (b.id === id ? fn(b) : b)
  client.setQueryData<Bill[]>(keys.recurring.list(), (old) => old?.map(touch))
  client.setQueryData<Bill>(keys.recurring.detail(id), (old) => (old ? touch(old) : old))
  client.setQueriesData({ queryKey: keys.recurring.calendars() }, (old: unknown) =>
    isEntries(old) ? old.map((e) => (e.bill.id === id ? { ...e, bill: touch(e.bill) } : e)) : old,
  )
}

/** What `onMutate` changed, so a rollback puts back exactly that. */
type Snapshot = { list: Bill[] | undefined; detail: Bill | undefined; calendars: [QueryKey, unknown][] }

const isEntries = (d: unknown): d is Entry[] => Array.isArray(d)

/** The bill as the pause would leave it, before the server has said so. */
const withPaused = (b: Bill, paused: boolean): Bill => ({
  ...b,
  isPaused: paused,
  isActive: !paused && !b.isRemoved,
})

async function send(bill: Bill, op: Op): Promise<Bill | null> {
  const path = { recurring_id: bill.id }
  let result
  try {
    if (op.op === 'skip') {
      // Always the period, never an empty body: the server would skip whatever
      // period it likes (S9, #809).
      result = await api.POST('/api/v1/recurring/{recurring_id}/skip', {
        params: { path },
        body: { period_due_date: op.periodDueDate },
      })
    } else if (op.op === 'pause') {
      // BOTH signals: the server reads either (S10). `version` is the one this
      // screen holds; a mismatch is the 409, not something to paper over.
      result = await api.PATCH('/api/v1/recurring/{recurring_id}', {
        params: { path },
        body: op.paused
          ? { status: 'paused', is_paused: true, version: bill.version }
          : { status: 'active', is_paused: false, version: bill.version },
      })
    } else {
      result = await api.DELETE('/api/v1/recurring/{recurring_id}', { params: { path } })
    }
  } catch (cause) {
    throw new LifecycleError('failed', failedCopy(op), cause)
  }
  const { data, error, response } = result
  if (error === undefined) return op.op === 'pause' && data !== undefined ? toBill(data as Parameters<typeof toBill>[0]) : null
  // A bill already removed is the state a delete was after.
  if (op.op === 'delete' && response.status === 404) return null
  if (op.op === 'pause' && response.status === 409) throw new LifecycleError('conflict', CONFLICT_COPY, error)
  throw new LifecycleError('failed', failedCopy(op), error)
}

function failedCopy(op: Op): string {
  if (op.op === 'skip') return SKIP_FAILED
  if (op.op === 'delete') return DELETE_FAILED
  return op.paused ? PAUSE_FAILED : RESUME_FAILED
}

/**
 * M7-R16, S9, S10, S13: skip one payment, pause or resume a bill, or delete it.
 *
 * **One lock per bill, in the mutation cache** (`keys.recurring.lifecycle`),
 * the same shape as `useMarkPaid`: a row that unmounts mid-write and remounts
 * still sees it. Unlike mark-paid a repeat is not folded into the first — a
 * second verb is a different intent, so it is refused (`busy`) and nothing is
 * sent.
 *
 * **Pause is optimistic and unconfirmed; skip and delete are neither.** The
 * pause flips the bill in the list, the detail and every calendar month at
 * once (no motion, M0-R20), and rolls exactly that back on failure. Skip's
 * outcome is a server fact — whether the due date moved — so it waits
 * and the refetch shows it.
 *
 * **409 means refetch once, never retry.** The settle invalidates the whole
 * `['recurring']` root, which refetches each active read once — that IS the
 * one refetch. Replaying the PATCH with the new `version` would overwrite
 * whatever the other device changed.
 *
 * Mutation-level callbacks, so the rollback and toasts still run when the row
 * that started it has unmounted. `skip` and `remove` reject on failure so the
 * confirm dialog can show `error.message` and stay open.
 */
export function useLifecycle(bill: Bill): {
  skip: (periodDueDate: LocalDate) => Promise<void>
  setPaused: (paused: boolean, options?: { quiet?: boolean }) => Promise<void>
  remove: () => Promise<void>
  busy: boolean
} {
  const client = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const mutationKey = keys.recurring.lifecycle(bill.id)
  const busy = useMutationState({ filters: { mutationKey, exact: true, status: 'pending' } }).length > 0

  const mutation = useMutation<Bill | null, LifecycleError, Op, Snapshot | undefined>({
    mutationKey,
    mutationFn: (op) => send(bill, op),
    onMutate: async (op) => {
      if (op.op !== 'pause') return undefined
      await client.cancelQueries({ queryKey: keys.recurring.all() })
      const snapshot: Snapshot = {
        list: client.getQueryData<Bill[]>(keys.recurring.list()),
        detail: client.getQueryData<Bill>(keys.recurring.detail(bill.id)),
        calendars: client.getQueriesData({ queryKey: keys.recurring.calendars() }),
      }
      patchBill(client, bill.id, (b) => withPaused(b, op.paused))
      return snapshot
    },
    onSuccess: (updated, op) => {
      // The series a dashboard counts; ConfirmDialog's `confirmation_result` is the skip/delete funnel.
      emitLifecycle(op.op === 'pause' ? (op.paused ? 'pause' : 'resume') : op.op)
      if (op.op === 'pause' && updated !== null) {
        // The server's word: the new `version`, and whatever else it moved.
        patchBill(client, bill.id, () => updated)
      }
      if (op.op === 'delete') {
        navigate('/recurring')
        toast.show({ kind: 'success', message: DELETED_COPY })
      }
    },
    onError: (error, op, snapshot) => {
      if (snapshot !== undefined) {
        client.setQueryData(keys.recurring.list(), snapshot.list)
        client.setQueryData(keys.recurring.detail(bill.id), snapshot.detail)
        for (const [queryKey, data] of snapshot.calendars) client.setQueryData(queryKey, data)
      }
      if (op.op === 'pause' && op.quiet) return
      if (error.kind === 'conflict') toast.show({ kind: 'info', message: CONFLICT_COPY })
      // Skip and delete report through their confirm dialog, which stays open.
      else if (op.op === 'pause') toast.show({ kind: 'error', message: error.message })
    },
    // Not awaited: the caller settles on the server's answer, and a slow
    // refetch must not hold the lock.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
    },
  })

  const { mutateAsync } = mutation
  const run = useCallback(
    async (op: Op): Promise<void> => {
      const cache = client.getMutationCache()
      if (cache.findAll({ mutationKey: keys.recurring.lifecycle(bill.id), exact: true, status: 'pending' }).length > 0) {
        throw new LifecycleError('busy', BUSY_COPY)
      }
      await mutateAsync(op)
    },
    [client, bill.id, mutateAsync],
  )

  return {
    skip: useCallback((periodDueDate: LocalDate) => run({ op: 'skip', periodDueDate }), [run]),
    setPaused: useCallback((paused: boolean, options?: { quiet?: boolean }) => run({ op: 'pause', paused, quiet: options?.quiet }), [run]),
    remove: useCallback(() => run({ op: 'delete' }), [run]),
    busy,
  }
}
